import React, { useState, useEffect } from 'react';
import styles from '../../styles/ProductPage.module.css';
import { supabase } from '../../lib/supabase';

interface Review {
  id: string;
  product_id: string;
  customer_name: string;
  rating: number;
  review_text: string;
  image_url: string | null;
  created_at: string;
}

interface ProductReviewsProps {
  productId: string;
}

const ProductReviews: React.FC<ProductReviewsProps> = ({ productId }) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error } = await supabase
          .from('reviews')
          .select('*')
          .eq('product_id', productId)
          .eq('is_approved', true)
          .order('created_at', { ascending: false });

        if (error) {
          throw error;
        }

        setReviews(data || []);
      } catch (error) {
        console.error('Error fetching reviews:', error);
        setError('Greška prilikom učitavanja recenzija.');
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchReviews();
    }
  }, [productId]);

  if (loading) {
    return <div className={styles.noReviews}>Učitavanje recenzija...</div>;
  }

  if (error) {
    return <div className={styles.noReviews}>{error}</div>;
  }

  if (reviews.length === 0) {
    return null; // Ne prikazujemo ništa ako nema recenzija
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // Koristitimo native Date API
    return date.toLocaleDateString('sr-Latn-RS', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className={styles.reviewsSection}>
      <h2>Recenzije korisnika</h2>
      <div className={styles.reviewsList}>
        {reviews.map((review) => (
          <div key={review.id} className={styles.reviewCard}>
            <div className={styles.reviewHeader}>
              <div className={styles.reviewCustomer}>{review.customer_name}</div>
              <div className={styles.reviewDate}>{formatDate(review.created_at)}</div>
            </div>
            <div className={styles.reviewRating}>
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={star <= review.rating ? styles.filled : ''}
                >
                  ★
                </span>
              ))}
            </div>
            <div className={styles.reviewContent}>{review.review_text}</div>
            {review.image_url && (
              <div className={styles.reviewImage}>
                <img src={review.image_url} alt="Slika recenzije" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductReviews; 